import React, {useContext, useEffect, useState} from 'react';
import {useRouter} from 'next/router';
import {ClockIcon, Squares2X2Icon, UsersIcon} from '@heroicons/react-v2/24/outline';
import {SparklesIcon} from '@heroicons/react-v2/16/solid';
import {compact} from 'lodash';
import Image from 'next/image';

import DocumentHeader from '../../components/DocumentHeader';
import {CourseCarousel, Course as CourseData} from '../../components/LinkedData';
import {
  useAppContext, useAnalytics, useScrollPosition, useScrollToHash,
} from '../../hooks';
import SignupModal from '../../components/SignupModal';
import FAQSection from '../../components/FAQSection';
import Button, {ButtonSizes} from '../../components/ButtonNew';
import StickyHeader from '../../components/StickyHeader';
import Testimonial from '../../components/TestimonialCard';
import Link from '../../components/Link';

import {getCourseStatistics, getTrackStatistics, getNextAvailableLesson} from '../../util/courses';
import {getMonthlyPriceStr, formatMinutes} from '../../util';
import {CourseVisits} from '../../../../lib/constants/courses';

import CourseHero from './CourseHero';
import CoursePanel from './CoursePanel';
import CourseAuthors from './CourseAuthors';
import CompanySocialProof from './CompanySocialProof';
import RelatedCourses from './RelatedCourses';
import CourseLessonsProvider, {CourseLessonsContext} from '../../components/CourseLessonsProvider';

import QuestionsFeatureImg from '../../../assets/images/courses/questions.svg';
import SlackFeatureImg from '../../../assets/images/courses/slack.svg';
import GuidesFeatureImg from '../../../assets/images/courses/guides.svg';
import DuetFeedback from '../../../public/images/duet_feedback.png';

const Features = [
  {
    title: 'Question database and community answers',
    description: `
      Get full access to our database of 3,000+ verified interview questions
      and browse by company, role, or interview stage. Post your answers
      and get community feedback.
    `,
    image: <Image src={QuestionsFeatureImg} alt="Question database" />,
  },
  {
    title: 'Access all our company interview guides',
    description: `
      We'll walk you step-by-step through every part of the
      interview process, from the recruiting phone screen through
      the technical screen.
    `,
    image: <Image src={GuidesFeatureImg} alt="Airbnb interview guide" />,
  },
  {
    title: 'Join our members-only Slack community',
    description: `
      Connect with your peers in our 25,000-member strong Slack community.
      Ask questions, practice interviews, and make connections at
      top tech companies.
    `,
    image: <Image src={SlackFeatureImg} alt="Slack app" />,
  },
  {
    title: 'Mock interviews with AI and peers',
    description: `
      Practice real interview questions with unlimited mock interviews,
      including early access to our new AI interviewer.
    `,
    image: <Image src={DuetFeedback} alt="Peer mock interview session" className="-mb-3 object-contain" />,
  },
];

const FeatureCard = ({feature}) => (
  <div className="flex flex-col flex-1 border border-gray-200 shadow-sm rounded-lg overflow-hidden">
    <div className="h-40 flex p-4 pb-0 justify-center bg-gray-50 border-b border-gray-200">
      {feature.image}
    </div>
    <div className="p-3 flex flex-col gap-1.5">
      <span className="text-base font-semibold text-gray-900 leading-tight">
        {feature.title}
      </span>
      <span className="text-sm font-regular text-gray-600">
        {feature.description}
      </span>
    </div>
  </div>
);

const CoursePageComponent = ({courseSlug}) => {
  const {
    catalog,
    currency,
    currentUser,
    hasSubscription,
  } = useAppContext();
  const {courses, loading} = useContext(CourseLessonsContext);
  const {tracker} = useAnalytics();
  const [isSignupOpen, setSignupOpen] = useState(false);
  const router = useRouter();
  const scrollPosition = useScrollPosition();

  useEffect(() => {
    const course = courses.find((c) => c.slug === courseSlug);
    if (!loading && courseSlug && course) {
      tracker.courseView({id: courseSlug, course: courseSlug});
    }
  }, [courseSlug]);

  // wait for courses to load before applying hash/anchor scroll
  useScrollToHash(loading);

  if (loading || !courseSlug) return null;

  const item = courses.find((c) => c.slug === courseSlug);

  if (!item) {
    router.push('/404');
    return null;
  }

  const isTrack = item?.contentType === 'track';
  const isCourse = item?.contentType === 'course';
  const trackSlug = isCourse ? null : item.slug;
  const nextLesson = getNextAvailableLesson({
    courses: isCourse ? [item] : item.courses,
    trackSlug,
    hasSubscription,
  });
  const studentCount = CourseVisits[item.slug];

  // Currently, track names are suffixed with "Course" already, whereas courses aren't
  const pageTitle = isTrack ? item.shortTitle : `${item.shortTitle} Course`;

  let nextLessonURL;

  if (nextLesson) {
    nextLessonURL = isCourse
      ? `/courses/${nextLesson.courseSlug}/${nextLesson.slug}`
      : `/courses/${nextLesson.trackSlug}/${nextLesson.courseSlug}/${nextLesson.slug}`;
  }

  const upgradeURL = `/upgrade?course=${item.slug}&src=course`;

  const onClickUpgrade = (source) => {
    tracker.courseClickUpgrade({courseID: item.slug, type: source});
  };

  const onClickContinue = (e) => {
    if (!currentUser) {
      tracker.courseViewSignupWall({courseID: item.slug});
      setSignupOpen(true);
      e.preventDefault();
      return;
    }

    if (hasSubscription) {
      tracker.courseClickContinue({courseID: item.slug});
    } else {
      tracker.courseClickFreeLesson({courseID: item.slug});
    }
  };

  const onClickLesson = (e, lesson, _courseSlug, _trackSlug) => {
    tracker.courseClickLesson({
      slug: lesson.slug,
      courseID: _courseSlug,
      trackSlug: _trackSlug,
      comingSoon: lesson.comingSoon,
    });

    if (!currentUser) {
      tracker.courseViewSignupWall({
        slug: lesson.slug,
        courseID: _courseSlug,
        trackSlug: _trackSlug,
      });
      setSignupOpen(true);
      e.preventDefault();
    }

    if (lesson.comingSoon) {
      e.preventDefault();
    }
  };

  const {min, hours} = isCourse
    ? getCourseStatistics(item)
    : getTrackStatistics(item);

  const timeCountLabel = min < 60
    ? `${min} minutes`
    : `${hours} ${hours === 1 ? 'hour' : 'hours'}`;

  const monthlyPrice = catalog && currency
    ? getMonthlyPriceStr(currency.symbol, catalog.MEMBERSHIP_ANNUAL.price)
    : 'Loading...';

  const navigationTabs = compact([
    item.testimonials?.length > 0 && {key: 'reviews', label: 'Reviews', count: item?.testimonials?.length},
    {key: 'contents', label: 'Curriculum'},
    {key: 'features', label: 'Features'},
    item.faq?.length > 0 && {key: 'faq', label: 'FAQ'},
  ]);

  return (
    <div className="flex flex-col items-center bg-white">
      <DocumentHeader
        title={pageTitle}
        imgPath={item.socialThumbnail}
        socialDescription={item.description}
        metaDescription={item.description}
      />
      {isTrack
        ? <CourseCarousel courses={item.courses} />
        : <CourseData name={item.title} description={item.description} />}
      <SignupModal isOpen={isSignupOpen} source="course" />

      {/* Sticky header */}
      {!hasSubscription && (
        <StickyHeader
          navigationTabs={navigationTabs}
          cta="Upgrade now"
          href={upgradeURL}
          onClick={() => onClickUpgrade('header')}
        />
      )}

      {/* Hero section */}
      <CourseHero
        course={item}
        upgradeURL={upgradeURL}
        nextLessonURL={nextLessonURL}
        monthlyPrice={monthlyPrice}
        onClickUpgrade={() => onClickUpgrade('hero')}
        onClickContinue={onClickContinue}
        studentCount={studentCount}
      />

      {/* AI interview CTA */}
      {item.aiInterviewType && (
        <Link
          href={`/practice/ai/new?type=${item.aiInterviewType}&src=course`}
          target="_blank"
          className="bg-indigo-200 w-full p-4 flex items-center justify-center text-indigo-700 text-sm font-medium"
        >
          <SparklesIcon className="w-4 h-4 mr-2 text-indigo-700" />

          {/* Desktop */}
          <span className="hidden sm:inline">
            Level up your interviewing with our AI mock interviewer and get feedback instantly.
            {' '}
            {hasSubscription ? 'Try now →' : 'Try for free →'}
          </span>

          {/* Mobile */}
          <span className="inline sm:hidden">
            Practice interviews with AI.
            {' '}
            {hasSubscription ? 'Try now →' : 'Try free →'}
          </span>

        </Link>
      )}

      {/* Company social proof section */}
      <CompanySocialProof courseSlug={courseSlug} />

      {/* Author section */}
      {item.contributors?.length > 1 && (
        <CourseAuthors
          authors={item.contributors}
          authorHeadline={item.authorHeadline}
        />
      )}

      {/* Testimonials */}
      {!hasSubscription && item.testimonials?.length > 0 && (
        <div className="flex justify-center w-full" id="reviews">
          <div className="flex flex-col max-w-7xl py-6 px-2 md:py-12 md:px-16">
            <h3 className="mb-2 text-xl lg:text-3xl font-bold text-gray-900 text-center">
              {`What people like about ${isTrack ? 'these courses' : 'this course'}`}
            </h3>
            <div className="mt-8 flex flex-col lg:flex-row items-stretch gap-4">
              {item.testimonials.slice(0, 3).map((testimonial) => (
                <div className="flex flex-1">
                  <Testimonial testimonial={testimonial} key={testimonial.name} />
                </div>
              ))}
            </div>
            <span className="mt-6 text-center font-medium text-indigo">
              <Link href="/reviews?src=course" target="_blank">
                {'See all reviews ->'}
              </Link>
            </span>
          </div>
        </div>
      )}

      <div className="w-full max-w-7xl py-6 px-2 md:py-10 md:px-16" id="contents">

        {/* Course contents */}
        <div className="grid grid-cols-12 mt-6">
          <div className="col-span-12 lg:col-span-8 lg:col-start-3">
            <div className="mb-3 text-xl lg:text-3xl font-bold text-center">
              {`What you'll find in ${isTrack ? 'these courses' : 'this course'}`}
            </div>

            {/* Track layout */}
            {isTrack && (
              <>
                <div className="mb-10 flex justify-center items-center space-x-3 text-gray-600">
                  <div className="flex items-center space-x-1 whitespace-nowrap">
                    <Squares2X2Icon className="h-5 w-5" />
                    <span>{`${item.courses.length} courses`}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <ClockIcon className="h-5 w-5" />
                    <span>{formatMinutes(item?.estimatedTime) || timeCountLabel}</span>
                  </div>
                </div>
                <div className="mt-4 space-y-4">
                  {item.courses.map((course) => (
                    <CoursePanel
                      course={course}
                      trackSlug={item.slug}
                      truncateAfter={5}
                      onClickLesson={onClickLesson}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Course layout */}
            {isCourse && (
              <div className="mt-8">
                <CoursePanel
                  course={item}
                  onClickLesson={onClickLesson}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Membership feature section */}
      {!hasSubscription && (
        <div className="flex justify-center w-full py-6 px-8 md:py-16 md:px-16 bg-white" id="features">
          <div className="flex flex-col w-full max-w-5xl justify-center">
            <h3 className="mb-4 text-xl lg:text-3xl font-bold text-gray-900 text-center">
              {`Get ${isTrack ? 'these courses' : 'this course'}, plus everything Exponent has to offer`}
            </h3>
            <Button
              size={ButtonSizes.LARGE}
              href={upgradeURL}
              className="my-4 mx-auto"
              onClick={() => onClickUpgrade('features')}
            >
              {'Upgrade now ->'}
            </Button>
            <div className="mt-6 flex flex-col md:flex-row gap-6">
              {Features.map((feature) => <FeatureCard feature={feature} key={feature.title} />)}
            </div>
          </div>
        </div>
      )}

      {/* FAQ */}
      {!hasSubscription && item.faq?.length > 0 && (
        <div className="flex justify-center w-full py-6 px-8 md:py-10 md:px-16 bg-gray-50" id="faq">
          <div className="w-full max-w-4xl">
            <h3 className="mb-4 text-xl lg:text-3xl font-bold text-gray-900 text-center">
              Frequently asked questions
            </h3>
            <FAQSection items={item.faq} />
          </div>
        </div>
      )}

      {item.relatedCourses?.length > 0 && (
        <div className="max-w-6xl mx-auto my-6 md:my-12">
          <RelatedCourses relatedCourses={item.relatedCourses} currSlug={item.slug} />
        </div>
      )}
    </div>
  );
};

const CoursePage = ({courseSlug}) => (
  <CourseLessonsProvider>
    <CoursePageComponent courseSlug={courseSlug} />
  </CourseLessonsProvider>
);

export default CoursePage;
